import { useEffect, useState } from 'react';
import { fmtMoney, type GbFinalChoice, type GbSeatView } from './types.js';

// ---------------------------------------------------------------------------
// The Split or Steal reveal
// ---------------------------------------------------------------------------
// This is a CEREMONY, not a state change: 3-2-1, both golden shells
// split open AT THE SAME INSTANT, the cards inside rise, and only then
// do the payouts land. Neither player learns anything a frame before the
// other.
//
// It lives on the ENDED phase, deliberately. The engine resolves the
// final and sets `ended` in one transition, so a viewer state never has
// phase 'final' WITH a result — a reveal rendered on the final phase can
// never run in a real match, however good it looks in a fixture. The
// debrief screen therefore opens with this, and only then hands over to
// the numbers.
//
// Landing on an already-finished match (a refresh, a shared link) skips
// the countdown: the drama belongs to the moment, not to the archive.

// SPLIT black on GOLD · STEAL white on RED · STING gold on BLACK.
// Three different grounds as well as three different inks, so no ball
// needs a rim or a second glance to tell it from another.
const FACE: Record<GbFinalChoice, { title: string; face: string; ink: string }> = {
  split: {
    title: 'Split',
    face: 'bg-gradient-to-br from-ec-brass to-ec-brass-deep',
    ink: 'text-black'
  },
  steal: {
    title: 'Steal',
    face: 'bg-gradient-to-br from-[#e02a1f] to-[#8c1109]',
    ink: 'text-white'
  },
  sting: {
    title: 'Sting',
    face: 'bg-gradient-to-br from-[#141210] to-black',
    ink: 'text-ec-brass'
  }
};

type Stage = 'counting' | 'opening' | 'settled';

function useCeremony(animate: boolean): { stage: Stage; count: number | null } {
  const [stage, setStage] = useState<Stage>(animate ? 'counting' : 'settled');
  const [count, setCount] = useState<number | null>(animate ? 3 : null);

  // No "already started" ref. Guarding on a ref while the cleanup clears
  // the timers means StrictMode's mount/unmount/remount leaves the
  // countdown frozen on 3 forever — which is exactly what it did.
  useEffect(() => {
    if (!animate) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setCount(null);
      setStage('settled');
      return;
    }
    setStage('counting');
    setCount(3);
    const ticks = [
      setTimeout(() => setCount(2), 850),
      setTimeout(() => setCount(1), 1700),
      setTimeout(() => {
        setCount(null);
        setStage('opening');
      }, 2550),
      setTimeout(() => setStage('settled'), 3550)
    ];
    return () => ticks.forEach(clearTimeout);
  }, [animate]);

  return { stage, count };
}

export function FinalCeremony({
  seats,
  jackpot,
  choices,
  payouts,
  destroyed,
  explanation,
  animate,
  onDone
}: {
  seats: GbSeatView[];
  jackpot: number;
  choices: Record<string, GbFinalChoice>;
  payouts: Record<string, number>;
  destroyed: number;
  explanation: string;
  /** False when arriving at a match that finished before you opened it. */
  animate: boolean;
  onDone: () => void;
}) {
  const { stage, count } = useCeremony(animate);
  // Whoever actually chose — the two finalists, in seat order.
  const finalists = seats.filter((seat) => choices[seat.seatId] !== undefined);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="shrink-0 rounded-lg border border-ec-brass/50 bg-ec-brass/5 p-2 text-center">
        <p className="font-display text-[0.6rem] uppercase tracking-wide text-ec-bone-dim">
          The jackpot
        </p>
        <p className="font-display text-2xl font-bold tabular-nums text-ec-brass">
          {fmtMoney(jackpot)}
        </p>
      </div>

      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 overflow-y-auto">
        {stage === 'counting' ? (
          <>
            <p className="font-display text-xs uppercase tracking-[0.3em] text-ec-bone-dim">
              Balls open together in
            </p>
            <p
              key={count}
              className="animate-count-slam font-display text-8xl font-bold tabular-nums text-ec-brass motion-reduce:animate-none"
            >
              {count}
            </p>
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-start justify-center gap-6">
              {finalists.map((seat) => (
                <RevealedBall
                  key={seat.seatId}
                  label={seat.label}
                  choice={choices[seat.seatId]}
                  payout={payouts[seat.seatId] ?? 0}
                  opening={stage === 'opening'}
                />
              ))}
            </div>

            {stage === 'settled' && (
              <>
                {destroyed > 0 && (
                  <p className="animate-pop-in font-serif text-sm text-[#e02a1f] motion-reduce:animate-none">
                    {fmtMoney(destroyed)} destroyed.
                  </p>
                )}
                <p className="max-w-md animate-pop-in text-center font-serif text-sm text-ec-bone motion-reduce:animate-none">
                  {explanation}
                </p>
              </>
            )}
          </>
        )}
      </div>

      {/* Pinned, always reachable — including mid-countdown, so nobody is
          ever held hostage by the theatre. */}
      <button
        type="button"
        onClick={onDone}
        className="ec-btn ec-btn--gold min-h-[3rem] shrink-0 text-sm"
      >
        {stage === 'settled' ? 'See the full debrief' : 'Skip the reveal'}
      </button>
    </div>
  );
}

/**
 * One finalist's ball. The golden shell splits — top half up, bottom
 * half down — and the card inside rises through the gap. Both balls
 * mount on the same tick, so they open in lockstep.
 */
function RevealedBall({
  label,
  choice,
  payout,
  opening
}: {
  label: string;
  choice: GbFinalChoice | undefined;
  payout: number;
  opening: boolean;
}) {
  const copy = choice ? FACE[choice] : null;

  return (
    <div className="flex flex-col items-center gap-2">
      <span className="font-display text-xs uppercase tracking-wide text-ec-bone">{label}</span>

      <div className="relative h-28 w-28 [perspective:600px]">
        <div
          className={[
            'absolute inset-0 flex animate-reveal-card items-center justify-center rounded-full shadow-ec-art motion-reduce:animate-none',
            copy?.face ?? 'bg-ec-panel'
          ].join(' ')}
        >
          <span className="pointer-events-none absolute inset-x-5 top-2 h-1/3 rounded-full bg-white/10 blur-[2px]" />
          <span
            className={`font-display text-xl font-bold uppercase tracking-wide ${copy?.ink ?? 'text-ec-bone-dim'}`}
          >
            {copy?.title ?? '—'}
          </span>
        </div>

        {opening && (
          <>
            <span
              className="absolute inset-x-0 top-0 h-1/2 origin-bottom animate-shell-top overflow-hidden rounded-t-full bg-gradient-to-b from-ec-brass to-ec-brass-deep motion-reduce:hidden"
              aria-hidden="true"
            >
              <span className="absolute inset-x-5 top-2 h-1/2 rounded-full bg-white/25 blur-[2px]" />
            </span>
            <span
              className="absolute inset-x-0 bottom-0 h-1/2 origin-top animate-shell-bottom rounded-b-full bg-gradient-to-t from-ec-brass-deep to-ec-brass motion-reduce:hidden"
              aria-hidden="true"
            />
          </>
        )}
      </div>

      <span className="font-serif text-lg tabular-nums text-ec-brass">{fmtMoney(payout)}</span>
    </div>
  );
}
